/**
 * Düğün günü hava özeti — Open-Meteo Forecast API (istemci tarayıcı).
 *
 * ## Open-Meteo kullanımı ve lisans
 * - Ücretsiz katman: ticari olmayan kullanım, günlük/ saatlik kota sınırları;
 *   ticari ürünler için ücretli API planı gerekebilir. Güncel koşullar:
 *   https://open-meteo.com/en/terms — projeyi dağıtmadan önce uygunluğu doğrulayın.
 * - Veri CC-BY 4.0; üretimde yasal uygunluk için site harici (ör. alt bilgi) değerlendirin.
 *
 * ## Endpoint ve parametreler
 * `GET https://api.open-meteo.com/v1/forecast`
 * - `latitude`, `longitude` — mekân koordinatları (Firestore `venueCoords` ile aynı).
 * - `daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max`
 * - `hourly=temperature_2m,precipitation_probability,weather_code` — tören saati ve saatlik yağış olasılığı.
 * - `timezone=auto` — ızgarada o nokta için kullanılan saat dilimi (API yanıtıyla uyumlu).
 * - `start_date`, `end_date` — `YYYY-MM-DD`, **mekân saat diliminde** düğün takvim günü.
 *
 * ## Tarih/saat dilimi kuralı
 * - `tz-lookup(lat,lng)` ile IANA zaman dilimi alınır.
 * - “Bugün” ve “düğün günü” takvim tarihleri bu dilimde `en-CA` biçimiyle (`YYYY-MM-DD`) hesaplanır.
 * - Yerel takvim günleri arasındaki fark, tahmine ne zaman güvenilebileceğini belirler (>14 gün: API yok).
 */
import { Timestamp } from "firebase/firestore";
import tzlookup from "tz-lookup";
import type { WeddingDoc } from "./loadWedding";
import { getVenueLatLng } from "./loadWedding";
import type { Messages } from "../i18n/types";

const OPEN_METEO_FORECAST = "https://api.open-meteo.com/v1/forecast";
const CACHE_PREFIX = "wedy-openmeteo-v2-";
const TEMP_UNIT_STORAGE_KEY = "wedy.weatherTempUnit";

export type TempUnit = "c" | "f";

/** Diğer `wedy-card` başlıklarıyla uyumlu SVG — emoji yerine. */
const WEDY_WEATHER_HEAD_ICON = `<svg class="wedy-card__head-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6 14c-1.66 0-3-1.34-3-3 0-1.31.84-2.41 2-2.83V8c0-2.21 1.79-4 4-4s4 1.79 4 4v.17c1.16.42 2 1.52 2 2.83 0 1.66-1.34 3-3 3H6zm11-2c0-1.2-.54-2.27-1.38-3l.38-.17V8c0-2.76-2.24-5-5-5-2.1 0-3.9 1.29-4.64 3.12L7 6.1C7.61 4.23 9.62 3 12 3c3.31 0 6 2.69 6 6v.17c.84.73 1.38 1.8 1.38 3 0 2.21-1.79 4-4 4h-2v-2h2c1.1 0 2-.9 2-2z"/></svg>`;

export type WeatherBlockKind = "hidden" | "past" | "tooFar" | "forecast";

export type WeatherBlockState =
  | { kind: "hidden" }
  | { kind: "past" }
  | { kind: "tooFar" }
  | { kind: "forecast"; lat: number; lng: number; weddingDayISO: string };

/** Mekân TZ’de takvim günü YYYY-MM-DD. */
export function weddingCalendarDayISO(date: Date, timeZone: string): string {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(date);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  if (!y || !m || !d) return "";
  return `${y}-${m}-${d}`;
}

function calendarDaysBetweenUtcMidnight(aISO: string, bISO: string): number {
  const [ay, am, ad] = aISO.split("-").map(Number);
  const [by, bm, bd] = bISO.split("-").map(Number);
  const a = Date.UTC(ay, am - 1, ad);
  const b = Date.UTC(by, bm - 1, bd);
  return Math.round((b - a) / 86_400_000);
}

/**
 * Koordinat + tarih yoksa gizle; geçmiş / >14 gün / tahmin aralığı.
 * Tüm kararlar mekân IANA TZ’sinde takvim gününe göre.
 */
export function resolveWeatherBlockState(w: WeddingDoc): WeatherBlockState {
  const coords = getVenueLatLng(w);
  if (!coords) return { kind: "hidden" };
  if (w.hasSelectedDate === false) return { kind: "hidden" };
  const ts = w.date;
  if (!ts || !(ts instanceof Timestamp)) return { kind: "hidden" };

  let timeZone: string;
  try {
    timeZone = tzlookup(coords.lat, coords.lng);
  } catch {
    return { kind: "hidden" };
  }

  const weddingDate = ts.toDate();
  const todayISO = weddingCalendarDayISO(new Date(), timeZone);
  const weddingISO = weddingCalendarDayISO(weddingDate, timeZone);
  if (!todayISO || !weddingISO) return { kind: "hidden" };

  const diff = calendarDaysBetweenUtcMidnight(todayISO, weddingISO);
  if (diff < 0) return { kind: "past" };
  if (diff > 14) return { kind: "tooFar" };
  return {
    kind: "forecast",
    lat: coords.lat,
    lng: coords.lng,
    weddingDayISO: weddingISO,
  };
}

/** Etkinlik anının mekân TZ’de saat:dakika (24 saat, HH:MM). */
function ceremonyLocalHm(instant: Date, timeZone: string): string | null {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(instant);
  const h = parts.find((p) => p.type === "hour")?.value;
  const m = parts.find((p) => p.type === "minute")?.value;
  if (h == null || m == null) return null;
  return `${h}:${m}`;
}

function ceremonyHmForWeather(w: WeddingDoc, lat: number, lng: number): string | null {
  if (w.isAllDay === true) return null;
  const ts = w.date;
  if (!ts || !(ts instanceof Timestamp)) return null;
  let timeZone: string;
  try {
    timeZone = tzlookup(lat, lng);
  } catch {
    return null;
  }
  return ceremonyLocalHm(ts.toDate(), timeZone);
}

/** UI’da saat dilimi etkisi olmadan `HH:MM` → dil yerelleştirmeli kısa saat. */
export function formatHmForDisplay(hm: string, intlLocale: string): string {
  const [hs, ms] = hm.split(":");
  const h = Number(hs);
  const m = Number(ms);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hm;
  const ref = new Date(Date.UTC(2000, 0, 1, h, m));
  return new Intl.DateTimeFormat(intlLocale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(ref);
}

export function defaultTemperatureUnit(intlBcp47: string): TempUnit {
  try {
    const maximized = new Intl.Locale(intlBcp47).maximize();
    const r = maximized.region;
    if (r === "US" || r === "LR" || r === "MM") return "f";
  } catch {
    /* ignore */
  }
  return "c";
}

function readStoredTempUnit(): TempUnit | null {
  try {
    const v = localStorage.getItem(TEMP_UNIT_STORAGE_KEY);
    if (v === "c" || v === "f") return v;
  } catch {
    /* private mode */
  }
  return null;
}

export function effectiveTemperatureUnit(intlBcp47: string): TempUnit {
  return readStoredTempUnit() ?? defaultTemperatureUnit(intlBcp47);
}

function writeStoredTempUnit(u: TempUnit): void {
  try {
    localStorage.setItem(TEMP_UNIT_STORAGE_KEY, u);
  } catch {
    /* ignore */
  }
}

function cToDisplayTemp(c: number, unit: TempUnit): number {
  if (unit === "f") return Math.round((c * 9) / 5 + 32);
  return Math.round(c);
}

function formatSingleTempFromC(c: number | null | undefined, unit: TempUnit, msg: Messages): string {
  if (c == null || Number.isNaN(c)) return "";
  const n = cToDisplayTemp(c, unit);
  const suffix = unit === "f" ? msg.weatherUnitFahrenheit : msg.weatherUnitCelsius;
  return `${n}${suffix}`;
}

function formatTempRange(
  min: number | null | undefined,
  max: number | null | undefined,
  unit: TempUnit,
  msg: Messages,
): string {
  if (min != null && max != null) {
    const a = cToDisplayTemp(min, unit);
    const b = cToDisplayTemp(max, unit);
    const u = unit === "f" ? msg.weatherUnitFahrenheit : msg.weatherUnitCelsius;
    const range = msg.weatherTempRange.replace("{min}", String(a)).replace("{max}", String(b));
    const suffix = range.endsWith("°") ? `${range.slice(0, -1)}${u}` : `${range}${u}`;
    return suffix;
  }
  if (max != null) return formatSingleTempFromC(max, unit, msg);
  if (min != null) return formatSingleTempFromC(min, unit, msg);
  return "";
}

type OpenMeteoForecast = {
  daily?: {
    time?: string[];
    weather_code?: (number | null)[];
    temperature_2m_max?: (number | null)[];
    temperature_2m_min?: (number | null)[];
    precipitation_probability_max?: (number | null)[];
  };
  hourly?: {
    time?: string[];
    temperature_2m?: (number | null)[];
    precipitation_probability?: (number | null)[];
    weather_code?: (number | null)[];
  };
};

function cacheKey(lat: number, lng: number, dayISO: string): string {
  return `${CACHE_PREFIX}${lat.toFixed(4)}_${lng.toFixed(4)}_${dayISO}`;
}

function readCache(key: string): OpenMeteoForecast | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as OpenMeteoForecast;
  } catch {
    return null;
  }
}

function writeCache(key: string, data: OpenMeteoForecast): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

export async function fetchWeddingDayForecast(
  lat: number,
  lng: number,
  weddingDayISO: string,
): Promise<OpenMeteoForecast | null> {
  const key = cacheKey(lat, lng, weddingDayISO);
  const cached = readCache(key);
  if (cached?.daily?.time?.length) return cached;

  const url = new URL(OPEN_METEO_FORECAST);
  url.searchParams.set("latitude", String(lat));
  url.searchParams.set("longitude", String(lng));
  url.searchParams.set(
    "daily",
    "weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max",
  );
  url.searchParams.set("hourly", "temperature_2m,precipitation_probability,weather_code");
  url.searchParams.set("timezone", "auto");
  url.searchParams.set("start_date", weddingDayISO);
  url.searchParams.set("end_date", weddingDayISO);

  const res = await fetch(url.toString());
  if (!res.ok) return null;
  const json = (await res.json()) as OpenMeteoForecast;
  if (json.daily?.time?.length) writeCache(key, json);
  return json;
}

export function weatherCodeLabel(code: number | null | undefined, msg: Messages): string {
  if (code == null || Number.isNaN(code)) return msg.weatherUnknown;
  const c = code;
  if (c === 0 || c === 1) return msg.weatherClear;
  if (c === 2 || c === 3) return msg.weatherCloudy;
  if (c === 45 || c === 48) return msg.weatherFog;
  if (c >= 51 && c <= 57) return msg.weatherDrizzle;
  if (c >= 61 && c <= 67) return msg.weatherRain;
  if (c >= 71 && c <= 77) return msg.weatherSnow;
  if (c >= 80 && c <= 82) return msg.weatherRainShowers;
  if (c >= 85 && c <= 86) return msg.weatherSnowShowers;
  if (c >= 95 && c <= 99) return msg.weatherThunderstorm;
  return msg.weatherUnknown;
}

function weatherEmoji(code: number | null | undefined): string {
  if (code == null) return "🌡️";
  const c = code;
  if (c === 0 || c === 1) return "☀️";
  if (c === 2) return "🌤️";
  if (c === 3) return "☁️";
  if (c === 45 || c === 48) return "🌫️";
  if (c >= 51 && c <= 57) return "🌦️";
  if (c >= 61 && c <= 67) return "🌧️";
  if (c >= 71 && c <= 77) return "❄️";
  if (c >= 80 && c <= 82) return "🌧️";
  if (c >= 85 && c <= 86) return "❄️";
  if (c >= 95 && c <= 99) return "⛈️";
  return "🌡️";
}

function findHourlyIndexForCeremony(
  hourlyTimes: string[],
  weddingDayISO: string,
  ceremonyHm: string,
): number {
  const [hs] = ceremonyHm.split(":");
  const h = Number(hs);
  if (!Number.isFinite(h)) return -1;
  const prefix = `${weddingDayISO}T${String(h).padStart(2, "0")}:`;
  return hourlyTimes.findIndex((t) => t.startsWith(prefix));
}

/**
 * Uzun adresleri hava kartı için kısaltır: tekrarlayan sokak satırlarını atar,
 * ardından virgülle ayrılmış parçalardan son ikisini (ör. ilçe, il) gösterir.
 */
function shortenVenueLabelForDisplay(raw: string): string {
  const parts = raw
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (parts.length <= 1) return raw.trim();

  const compact: string[] = [];
  for (const p of parts) {
    const prev = compact[compact.length - 1];
    if (prev && areRedundantAddressSegments(prev, p)) {
      if (p.length > prev.length) compact[compact.length - 1] = p;
      continue;
    }
    compact.push(p);
  }

  if (compact.length <= 2) return compact.join(", ");
  return compact.slice(-2).join(", ");
}

/** Aynı sokak/mahalle adının iki kez yazıldığı satırlar (ör. "Kardelen Sk. 2A" + "Kardelen Sk."). */
function areRedundantAddressSegments(a: string, b: string): boolean {
  const first = (s: string) =>
    s
      .toLowerCase()
      .replace(/ı/g, "i")
      .split(/\s+/)[0]
      ?.replace(/[^a-z0-9ğüşıöç]/gi, "") ?? "";
  const fa = first(a);
  const fb = first(b);
  if (fa.length < 4 || fb.length < 4 || fa !== fb) return false;
  const streetish = /\b(sk|cad|cd\.|cadde|sok|sokak|bulvar|mah|mah\.|mahallesi)\b/i;
  return streetish.test(a) && streetish.test(b);
}

/** Tahmin koordinatlarındaki mekân — kartta gösterilen isim veya adres. */
function venueDisplayLabel(w: WeddingDoc): string {
  const name = w.venueName?.trim();
  const addr = w.venueAddress?.trim();
  if (name) return name;
  if (addr) return shortenVenueLabelForDisplay(addr);
  return "";
}

export function buildWeatherSectionHtml(
  w: WeddingDoc,
  msg: Messages,
  intlBcp47: string,
): string {
  const state = resolveWeatherBlockState(w);
  if (state.kind === "hidden") return "";

  if (state.kind === "past") {
    return `<div class="wedy-card wedy-card--weather" role="region" aria-label="${escapeAttr(msg.weatherTitle)}">
  ${sectionHead(msg.weatherTitle)}
  <div class="wedy-card__body">
    <p class="wedy-subtext wedy-weather__line">${escapeHtml(msg.weatherPastEvent)}</p>
  </div>
</div>`;
  }

  if (state.kind === "tooFar") {
    return `<div class="wedy-card wedy-card--weather" role="region" aria-label="${escapeAttr(msg.weatherTitle)}">
  ${sectionHead(msg.weatherTitle)}
  <div class="wedy-card__body">
    <p class="wedy-subtext wedy-weather__line">${escapeHtml(msg.weatherForecastTooFar)}</p>
  </div>
</div>`;
  }

  const { lat, lng, weddingDayISO } = state;
  const venueLabel = venueDisplayLabel(w);
  const ceremonyHm = ceremonyHmForWeather(w, lat, lng);
  const venueAttr =
    venueLabel.length > 0 ? ` data-wedy-weather-venue="${escapeAttr(venueLabel)}"` : "";
  const ceremonyAttr =
    ceremonyHm != null && ceremonyHm.length > 0
      ? ` data-wedy-weather-ceremony-hm="${escapeAttr(ceremonyHm)}"`
      : "";
  const localeAttr = ` data-wedy-weather-intl="${escapeAttr(intlBcp47)}"`;

  return `<div id="wedy-weather-host" class="wedy-card wedy-card--weather" role="region" aria-label="${escapeAttr(msg.weatherTitle)}"
  data-wedy-weather-lat="${lat}" data-wedy-weather-lng="${lng}" data-wedy-weather-day="${escapeAttr(weddingDayISO)}"${venueAttr}${ceremonyAttr}${localeAttr}>
  ${weatherForecastSectionHead(msg.weatherTitle)}
  <div class="wedy-card__body">
    <p class="wedy-subtext wedy-weather__line wedy-weather__loading">${escapeHtml(msg.loading)}</p>
  </div>
</div>`;
}

function sectionHead(title: string): string {
  return `<header class="wedy-card__head">
    <span class="wedy-card__head-icon" aria-hidden="true">${WEDY_WEATHER_HEAD_ICON}</span>
    <p class="wedy-label wedy-label--semibold wedy-card-section-title">${escapeHtml(title)}</p>
  </header>`;
}

/** Tahmin kartı — °C/°F seçici başlık satırının sağına yerleştirilir. */
function weatherForecastSectionHead(title: string): string {
  return `<header class="wedy-card__head wedy-card__head--weather">
    <span class="wedy-card__head-icon" aria-hidden="true">${WEDY_WEATHER_HEAD_ICON}</span>
    <p class="wedy-label wedy-label--semibold wedy-card-section-title">${escapeHtml(title)}</p>
    <div class="wedy-weather__head-unit"></div>
  </header>`;
}

function clearWeatherHeadUnit(host: HTMLElement): void {
  const slot = host.querySelector(".wedy-weather__head-unit");
  if (slot) slot.innerHTML = "";
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replaceAll("'", "&#39;");
}

function unitToggleHtml(unit: TempUnit, msg: Messages): string {
  const cPressed = unit === "c" ? "true" : "false";
  const fPressed = unit === "f" ? "true" : "false";
  const cCurrent = unit === "c" ? " wedy-weather__unit-btn--active" : "";
  const fCurrent = unit === "f" ? " wedy-weather__unit-btn--active" : "";
  return `<div class="wedy-weather__unit-toggle">
    <button type="button" class="wedy-weather__unit-btn${cCurrent}" data-wedy-temp-unit="c" aria-pressed="${cPressed}" aria-label="${escapeAttr(msg.weatherAriaTempCelsius)}">${escapeHtml(msg.weatherUnitCelsius)}</button>
    <span class="wedy-weather__unit-sep" aria-hidden="true">/</span>
    <button type="button" class="wedy-weather__unit-btn${fCurrent}" data-wedy-temp-unit="f" aria-pressed="${fPressed}" aria-label="${escapeAttr(msg.weatherAriaTempFahrenheit)}">${escapeHtml(msg.weatherUnitFahrenheit)}</button>
  </div>`;
}

function renderWeatherBody(
  host: HTMLElement,
  data: OpenMeteoForecast,
  day: string,
  ceremonyHm: string | undefined,
  intlBcp47: string,
  msg: Messages,
  unit: TempUnit,
): void {
  const body = host.querySelector(".wedy-card__body");
  if (!body) return;

  const daily = data.daily;
  const times = daily?.time ?? [];
  const idx = times.indexOf(day);
  if (idx < 0 || !daily) {
    body.innerHTML = `<p class="wedy-subtext wedy-weather__line">${escapeHtml(msg.weatherNoData)}</p>`;
    clearWeatherHeadUnit(host);
    return;
  }

  const code = daily.weather_code?.[idx] ?? null;
  const tmin = daily.temperature_2m_min?.[idx];
  const tmax = daily.temperature_2m_max?.[idx];
  const dayRain = daily.precipitation_probability_max?.[idx];

  const label = weatherCodeLabel(code, msg);
  const temps = formatTempRange(tmin, tmax, unit, msg);
  const emoji = weatherEmoji(code);

  const hourly = data.hourly;
  const hTimes = hourly?.time ?? [];
  let ceremonyHourRainPct: number | null = null;
  let ceremonyBlock = "";
  if (ceremonyHm && ceremonyHm.length > 0 && hTimes.length > 0) {
    const hi = findHourlyIndexForCeremony(hTimes, day, ceremonyHm);
    if (hi >= 0 && hourly) {
      const hc = hourly.temperature_2m?.[hi] ?? null;
      const hw = hourly.weather_code?.[hi] ?? null;
      const hp = hourly.precipitation_probability?.[hi];
      if (hp != null && !Number.isNaN(hp)) {
        ceremonyHourRainPct = Math.round(Math.max(0, Math.min(100, hp)));
      }
      const summary = weatherCodeLabel(hw, msg);
      const tStr = hc != null && !Number.isNaN(hc) ? formatSingleTempFromC(hc, unit, msg) : "";
      const timeDisp = formatHmForDisplay(ceremonyHm, intlBcp47);
      if (tStr && summary) {
        const ceremonyText = msg.weatherCeremonyLine
          .replace("{time}", timeDisp)
          .replace("{temp}", tStr)
          .replace("{summary}", summary);
        ceremonyBlock = `<p class="wedy-subtext wedy-weather__line">${escapeHtml(ceremonyText)}</p>`;
      }
    }
  }

  let rainPct: number | null = null;
  if (dayRain != null && !Number.isNaN(dayRain)) {
    rainPct = Math.round(Math.max(0, Math.min(100, dayRain)));
  } else if (ceremonyHourRainPct != null) {
    rainPct = ceremonyHourRainPct;
  }

  let rainDayBlock = "";
  if (rainPct != null) {
    const line = msg.weatherRainChanceDay.replace("{pct}", String(rainPct));
    rainDayBlock = `<p class="wedy-subtext wedy-weather__line">${escapeHtml(line)}</p>`;
  }

  body.innerHTML = `<div class="wedy-weather__row">
      <span class="wedy-weather__emoji" aria-hidden="true">${emoji}</span>
      <div class="wedy-weather__text">
        <span class="wedy-label">${escapeHtml(label)}${temps ? ` · ${escapeHtml(temps)}` : ""}</span>
        ${ceremonyBlock}
        ${rainDayBlock}
      </div>
    </div>`;

  const headUnit = host.querySelector(".wedy-weather__head-unit");
  if (headUnit) {
    headUnit.innerHTML = unitToggleHtml(unit, msg);
    bindTempUnitButtons(host, headUnit, data, day, ceremonyHm, intlBcp47, msg);
  }
}

function bindTempUnitButtons(
  host: HTMLElement,
  scope: ParentNode,
  data: OpenMeteoForecast,
  day: string,
  ceremonyHm: string | undefined,
  intlBcp47: string,
  msg: Messages,
): void {
  scope.querySelectorAll("[data-wedy-temp-unit]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const u = (btn as HTMLElement).dataset.wedyTempUnit;
      if (u !== "c" && u !== "f") return;
      writeStoredTempUnit(u);
      renderWeatherBody(host, data, day, ceremonyHm, intlBcp47, msg, u);
    });
  });
}

export async function hydrateWeatherIfPresent(
  root: HTMLElement,
  msg: Messages,
  intlBcp47: string,
): Promise<void> {
  const host = root.querySelector("#wedy-weather-host");
  if (!(host instanceof HTMLElement)) return;
  const lat = Number(host.dataset.wedyWeatherLat);
  const lng = Number(host.dataset.wedyWeatherLng);
  const day = host.dataset.wedyWeatherDay?.trim() ?? "";
  const ceremonyHm = host.dataset.wedyWeatherCeremonyHm?.trim();
  const intl =
    host.dataset.wedyWeatherIntl?.trim() && host.dataset.wedyWeatherIntl.trim().length > 0
      ? host.dataset.wedyWeatherIntl.trim()
      : intlBcp47;
  const body = host.querySelector(".wedy-card__body");
  if (!body) return;

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    clearWeatherHeadUnit(host);
    body.innerHTML = `<p class="wedy-subtext wedy-weather__line">${escapeHtml(msg.weatherLoadError)}</p>`;
    return;
  }

  try {
    const data = await fetchWeddingDayForecast(lat, lng, day);
    if (!data?.daily?.time?.length) {
      clearWeatherHeadUnit(host);
      body.innerHTML = `<p class="wedy-subtext wedy-weather__line">${escapeHtml(msg.weatherNoData)}</p>`;
      return;
    }
    const unit = effectiveTemperatureUnit(intl);
    renderWeatherBody(host, data, day, ceremonyHm, intl, msg, unit);
  } catch {
    clearWeatherHeadUnit(host);
    body.innerHTML = `<p class="wedy-subtext wedy-weather__line">${escapeHtml(msg.weatherLoadError)}</p>`;
  }
}
